import { DialogFooter, DialogIcon, Icon, Button } from '@startlibs/components'
import { Errors, IconRadioBoxText } from '@startlibs/form';
import { css } from 'styled-components'
import { darken } from 'polished';
import { getColor } from '@startlibs/utils'

export const createCustomColorTheme = (customColors) => ({
  startlibs: {
    DefaultStyle: css`
      .link {
        text-decoration: underline;
        font-weight: bold;
        color: ${getColor('main')};
        cursor: pointer;
        outline: 0;
        outline: none;
        :hover {
          color: ${props => darken(0.1, getColor("main")(props))};
        }
        &.gray {
          color: ${getColor('gray150')};
          :hover {
            color: ${getColor('main')};
          }
        }
      }
      .light-link {
        text-decoration: underline;
        color: ${getColor('main')};
        outline: 0;
        outline: none;
        cursor: pointer;
        :hover {
          color: ${props => darken(0.1, getColor("main")(props))};
        }
        &.gray {
          color: ${getColor('gray150')};
          :hover {
            color: ${getColor('main')};
          }
        }
      }
      a {
        cursor: pointer;
        &.link {
          text-decoration: underline;
          font-weight: 600;
          color: ${getColor('main')};
          cursor: pointer;
        }
        &.link.gray {
          color: ${getColor('gray150')};
          &:hover {
            color: ${getColor('main')};
          }
        }
      }
    }
    `,
    Button: css`
          box-shadow: inset 0 -3px 0 0 rgba(0,0,0,0.15);
          flex-shrink: 0;
          ${props => !props.rounded && `
            border-radius: 6px;
          `}
          ${props => props.small && `
            box-shadow: inset 0 -2px 0 0 rgba(0,0,0,0.15);
          `}
          ${Icon} {
            vertical-align: -5px;
          }
      `,
    ContextMenu: `
        box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0.5);
        border-radius: 6px;
        &.noIcons {
          a {
            padding-left: 1.5rem !important;
            padding-right: 1.5rem !important;
          }
        }
      `,
    Tooltip: `
      border-radius: 5px;
    `,
    Popup: `
        box-shadow: 0 0 3px 0 rgba(0, 0, 0, 0.4);
        border-radius: 4px;
      `,
    ListItem: css`
        ${props => props.hover && css`
          & > a, & > label {
            background-color: ${getColor('main')};
            color: white;
          }
        `}
        ${props => props.highlight && css`
            color: ${getColor('main')};
            font-weight: 600;
        `}
      `,
    Dialog: css`
        background-color: white;
        border-radius: 10px;
        max-width: 55rem;
        p {
          font-size: 13px;
        }
        h2 {
          border-bottom: none;
          padding-bottom: 0;
          font-size: 20px;
        }
        ${DialogFooter} {
          border-top: none;
          margin-bottom: -1rem;
          ${Button} {
            margin-bottom: 1rem;
          }
        }
        ${DialogIcon} {
          font-size: 100px;
        }
      `,
    FieldLabel: `
        font-weight: 600;
      `,
    TextInput: `
        border-radius: 6px;
      `,
    ToggleCheckbox: css`
        width: 2.75rem;
        height: 18px;
        border-radius: 18px;
        background: ${props => props.checked ? getColor('main') : 'rgba(0,0,0,0.2)'};
        display: inline-block;
        position: relative;
        font-weight: 600;
        :after {
          transition: none;
          width: 14px;
          height: 14px;
          background: white;
          ${props => props.checked ? css`
            margin-left: -2px;
            `: css`
            margin-left: 2px;
          `}
        }
      `,
    ToggleCheckboxBox: css`
        *, b, strong {font-size: 12px;}
        background: ${getColor('gray240')};
        :hover {
          background: ${props => darken(0.025, getColor("gray240")(props))};
        }
        :hover {
          background: ${props => darken(0.05, getColor("gray240")(props))};
        }
      `,
    IconRadioBox: css`
        overflow: hidden;
        border: 1px solid ${getColor('gray180')};
        :hover {
          background: rgba(0,0,0,0.025);
        }
        ${IconRadioBoxText} {
          padding: .75rem 1rem;
          background: inherit;
          border-top: 1px solid ${getColor('gray180')};
          border-bottom-right-radius: 5px;
          border-bottom-left-radius: 5px;
          box-shadow: inset 0 -3px 0 0 rgba(0,0,0,0.15);
        }
        ${props => props.checked && css`
          border: 1px solid ${getColor('main')};
          ${IconRadioBoxText} {
            background: ${getColor('main')};
            border-top: 1px solid ${getColor('main')};
            color: white;
          }
        `}
      `,
    ErrorItem: css`
        background: ${getColor('alert')};
        color: white;
        border: 0;
        ${Errors.CloseIcon} {
          color: white;
          background-color: rgba(0, 0, 0, 0.2);
          width: 20px;
          height: 20px;
          line-height: 20px;
          &:hover {
            background: rgba(0,0,0,0.25);
          }
        }
        a {
          color: white;
          font-weight: 600;
        }
      `
  },
  Field: { margins: { none: '0', big: '2rem', 'default': '1rem' } },
  colors: {
    gray30: '#1e1e1e',
    gray60: '#3c3c3c',
    gray90: '#5a5a5a',
    gray120: '#787878',
    gray150: '#969696',
    gray180: '#b4b4b4',
    gray210: '#d2d2d2',
    gray230: '#e6e6e6',
    gray240: '#f0f0f0',
    main: customColors?.main || '#008bd2',
    secondary: customColors?.secondary || '#1F70A0',
    alert: '#C3282D',
    warning: '#e6932e',
    success: '#39B54A',
    physician: '#00a99d',
    organization: '#ed1e79',
    mainBlue: "#008bd2",
    lightGreen: '#E7F2E9',
    lightYellow: '#F8F2DF',
    lightBlue: '#EFF4F7',
    bluishGray: '#ECEEF2',
    lightBluishGray: '#f1f3f5',
  },
  iconFont: 'purview-icons',
  icons: {
    'txt': '\ue96e',
    'ccda': '\ue96b',
    'xml': '\ue96c',
    'pathology': '\ue96d',
    'audit': '\ue96a',
    'star': '\ue969',
    'star-line': '\ue968',
    'divisions': '\ue967',
    'users-line': '\ue966',
    'external-report': '\ue965',
    'sign-doc': '\ue964',
    'svs': '\ue963',
    'user-line': '\ue95b',
    'physician-line': '\ue95c',
    'info': '\ue95d',
    'question-line': '\ue95e',
    'payment': '\ue95f',
    'clinical-sheet': '\ue960',
    'dicom-no-text': '\ue961',
    'email-line': '\ue962',
    'note': '\ue935',
    'delete': '\ue934',
    'help': '\ue933',
    'download': '\ue930',
    'print': '\ue931',
    'email': '\ue932',
    'arrow-up-line': '\ue92c',
    'arrow-down-line': '\ue92d',
    'arrow-left-line': '\ue92e',
    'arrow-right-line': '\ue92f',
    'hlogo-right': '\ue929',
    'hlogo-left': '\ue92a',
    'hlogo-center': '\ue92b',
    'reload': '\ue927',
    'image': '\ue928',
    'external-link-line': '\ue923',
    'edit': '\ue924',
    'reorder': '\ue925',
    'move': '\ue926',
    'bold': '\ue91b',
    'italic': '\ue91c',
    'underline': '\ue91d',
    'strikethrough': '\ue91e',
    'insertUnorderedList': '\ue91f',
    'insertOrderedList': '\ue920',
    'outdent': '\ue921',
    'indent': '\ue922',
    'sort': '\ue91a',
    'zip-file': '\ue919',
    'calendar': '\ue917',
    'lock': '\ue918',
    'lock-line': '\ue99e',
    'unlock-line': '\ue99d',
    'directories': '\ue913',
    'files': '\ue914',
    'failure': '\ue915',
    'external-link': '\ue916',
    'edit-box': '\ue911',
    'sign-out': '\ue912',
    'share': '\ue90a',
    'arrow-down': '\ue903',
    'arrow-up': '\ue904',
    'arrow-left': '\ue905',
    'arrow-right': '\ue906',
    'upload': '\ue907',
    'view': '\ue908',
    'settings': '\ue909',
    'search': '\ue90b',
    'check': '\ue90c',
    'x': '\ue90d',
    'plus-circle': '\ue90e',
    'notification': '\ue90f',
    'user': '\ue900',
    'physician': '\ue901',
    'organization': '\ue902',
    'report': '\ue936',
    'file': '\ue936',
    'checked-report': '\ue937',
    'station': '\ue938',
    'warning': '\ue939',
    'premium': '\ue93a',
    'credit-card': '\ue93b',
    'clock': '\ue93d',
    'zoom-in': '\ue93e',
    'zoom-out': '\ue93f',
    'minus': '\ue941',
    'plus': '\ue910',
    'home-line': '\ue940',
    'home': '\ue942',
    'hide': '\ue943',
    'send': '\ue944',
    'link': '\ue945',
    'pdf': '\ue947',
    'video': '\ue948',
    'image-line': '\ue949',
    'zip': '\ue94a',
    'dicom': '\ue94d',
    'unavailable': '\ue94f',
    'return': '\ue94e',
    'qrcode': '\ue950',
    'phone': '\ue951',
    'download-cd': '\ue952',
    'attachments': '\ue953',
    'launch': '\ue954',
    'send-line': '\ue955',
    'settings-line': '\ue956',
    'study-notes': '\ue957',
    'link-thin': '\ue958',
    'draggable': '\ue977',
    'draggable-corner': '\ue978',
    'move-group': '\ue979',
    'import': '\ue97a',
    'imported': '\ue97b',
    'remove-from-group': '\ue97c',
    'x-circle-undo': '\ue97d',
    'x-circle': '\ue97e',
    'start-line-ultra-light': '\ue97f',
    'feedback': '\ue980',
    'play': '\ue981',
    'split-doc': '\ue984',
    'check-files': '\ue986',
    'fontSizeUp': '\ue922',
    'fontSizeDown': '\ue922',
    'open-panel': '\ue987',
    'external-user': '\ue988',
    'copy': '\ue989',
    'table': '\ue98a',
    'delete-row': '\ue98b',
    'delete-column': '\ue98c',
    'add-row-below': '\ue98d',
    'add-row-above': '\ue98e',
    'add-column-right': '\ue98f',
    'add-column-left': '\ue992',
    'merge-cells': '\ue990',
    'split-cells': '\ue991',
    'dashboard': '\ue993',
    'csv': '\ue997',
    'hospital': '\ue998',
    'jurisdictions': '\ue99c',
    'analytics': '\ue9a0',
    'collaboration': '\ue9a1',
    'route': '\ue99b',
  }
})